import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import ConfirmationUseCustomConfigurationModal from './ConfirmationUseCustomConfigModal';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import InputField from 'src/presentation/components/forms/InputField';
import RadioGroup from 'src/presentation/components/forms/RadioGroup';
import dataRetentionConfigurationResource from 'src/actions/dataRetention/dataRetentionConfigResource';
import { AlertError } from '@ad-scripts/presentation/components/common/alert';
import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { FieldWrapper } from '@ad-scripts/presentation/components/common/input_field';
import { RadioButtonLabel } from '@ad-scripts/presentation/components/common/radio_button';

const DataRetentionConfigurationForm: FC<FormEditorProps> = ({ item, errors }) => {
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);
  const dispatch = useDispatch();
  const { replace, validate } = dataRetentionConfigurationResource.effects;
  const { resetErrors } = dataRetentionConfigurationResource.actions as any;

  const formContext = useForm({
    item,
    errors,
    onSave: ({ item, workingStateRef }) => {
      const currentWorkingState = workingStateRef.current;
      dispatch(resetErrors());
      const isValid = !!dispatch(validate(currentWorkingState));
      if (!isValid) {
        return;
      }

      if (!item.enabled && currentWorkingState.enabled) {
        setIsConfirmationModalVisible(true);
        return;
      }

      dispatch(replace({ id: currentWorkingState.id }, currentWorkingState));
    },
    resetErrors: dataRetentionConfigurationResource.actions.resetErrors,
    root: 'data-retention-config',
    update: replace,
    validate,
  });
  const { onSave, workingState } = formContext;
  const hasChanged = item.enabled !== workingState.enabled || item.retentionDays !== workingState.retentionDays;

  const customConfigurationEnabled = workingState.enabled || false;

  const saveHandler = () => {
    setIsConfirmationModalVisible(false);
    dispatch(replace({ id: workingState.id }, workingState));
  };

  return (
    <>
      <Form context={formContext}>
        <Body data-aid="editor" style={{ maxWidth: '750px' }}>
          <Section>
            <StyledRadioGroup
              className="radioGroup"
              attr="enabled"
              data-aid="enabled-radio-group"
              options={[
                {
                  label: 'Use default settings',
                  value: false,
                  description: 'All customer data is retained forever.',
                },
                {
                  label: 'Use custom settings',
                  value: true,
                  description: (
                    <>
                      Customer profiles, including any attachments and recordings, with conversations marked as closed
                      will be automatically deleted after a set amount of time. Minimum amount of time is 365 days.
                    </>
                  ),
                },
              ]}
            />

            {customConfigurationEnabled && (
              <>
                <StyledRow>
                  <StyledInput
                    action={<>days</>}
                    attr="retentionDays"
                    className="retentionDaysInput"
                    data-aid="retentionDays"
                    inputType="number"
                    label="Delete customer data after"
                    placeholder="366"
                  />
                </StyledRow>
                <StyledRow>
                  <AlertError>
                    <b>Note:&nbsp;</b>
                    Once customer profiles and conversations are deleted, they cannot be recovered.
                  </AlertError>
                </StyledRow>
              </>
            )}
          </Section>
        </Body>

        <Footer>
          <Button data-aid={'editor-save'} onClick={onSave} disabled={!hasChanged}>
            Save
          </Button>
        </Footer>
      </Form>
      {isConfirmationModalVisible && (
        <ConfirmationUseCustomConfigurationModal
          closeModalHandler={() => setIsConfirmationModalVisible(false)}
          retentionDays={workingState.retentionDays}
          onSave={saveHandler}
        />
      )}
    </>
  );
};
export default DataRetentionConfigurationForm;

const StyledRow = styled(Row)`
  padding-left: 20px;
  margin-bottom: ${({ theme }) => theme.spacing.insetMedium};

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledRadioGroup = styled(RadioGroup)`
  ${RadioButtonLabel} {
    font-weight: 700;
  }
`;

const StyledInput = styled(InputField)`
  ${FieldWrapper} {
    max-width: 100px;

    input {
      width: 100%;
    }
  }
`;
